import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../Hooks/useAPI";
import { User, useUser } from "./UserContext";
import { CookieService } from "../services/CookieService";

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(
    CookieService.getAuthToken()
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { verifyToken, addUserTracking } = useAPI();

  const { user, setUser, setImpersonating } = useUser();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const logout = useCallback(() => {
    const metadata = JSON.stringify({
      app: "IceGems",
      device: isMobile ? "mobile" : "desktop",
    });
    user && addUserTracking(user.id, "Logout", user.id, metadata);
    setToken(null);
    CookieService.removeAuthToken();
    CookieService.removeUserData();
    CookieService.removeImpersonating();
    setUser(null);
    setImpersonating(false); // Reset impersonation state
    navigate("/user-login");
  }, [navigate, setUser, setImpersonating]);

  const verifyAndSetAuth = useCallback(
    async (storedToken: string) => {
      setIsLoading(true);
      try {
        // Verify the token with your backend
        const isValid = await verifyToken(storedToken);

        if (isValid) {
          setToken(storedToken);
        } else {
          // If token is invalid, clear the stored data
          logout(); // Call logout to clear session and navigate to login
        }
      } catch (error) {
        console.error("[AuthProvider] Error verifying token:", error);
        logout(); // Call logout on error as well
      } finally {
        setIsLoading(false);
      }
    },
    [verifyToken, logout]
  );

  useEffect(() => {
    const storedToken = CookieService.getAuthToken();

    if (storedToken) {
      verifyAndSetAuth(storedToken);
    } else {
      setIsLoading(false); // No token to verify, set loading to false
    }
  }, [verifyAndSetAuth]);

  // Add visibility change listener to check auth when user returns to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const storedToken = CookieService.getAuthToken();
        if (storedToken) {
          verifyAndSetAuth(storedToken);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [verifyAndSetAuth]);

  const login = useCallback((newToken: string, userData: User) => {
    setToken(newToken);
    CookieService.setAuthToken(newToken);
  }, []);

  const value = useMemo(
    () => ({
      token,
      isAuthenticated: !!token,
      isLoading,
      login,
      logout,
    }),
    [token, isLoading, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
